import React, { useEffect, useState, useCallback } from "react";
import { Box, Container, Paper, Typography, useTheme, Alert, IconButton, Collapse } from "@mui/material";
import { Close as CloseIcon, Warning as WarningIcon } from "@mui/icons-material";
import FeatureCardList from "../FeatureCardList";
import PropTypes from "prop-types";
import PushPinIcon from '@mui/icons-material/PushPin';
import persistentStorageService from "../../../services/persistent-storage-service";

const PinnedItemsEmptyState = () => {
  const theme = useTheme();
  
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        p: { xs: 2, sm: 4 }
      }}
    >
      <Box sx={{ maxWidth: '800px', width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: (theme) => theme.palette.mode === 'light' ? '#E3F2FD' : '#1e3a5f',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <PushPinIcon sx={{ fontSize: 40, color: (theme) => theme.palette.mode === 'light' ? '#1976d2' : '#90caf9' }} />
          </Box>
        </Box>

        <Typography
          variant="h4"
          sx={{
            textAlign: 'center',
            fontWeight: 'bold',
            mb: 2,
            color: theme.palette.text.primary
          }}
        >
          No Pinned Items
        </Typography>

        <Typography
          sx={{
            textAlign: 'center',
            mb: 4,
            fontSize: '0.95rem',
            color: theme.palette.text.secondary
          }}
        >
          Pin your frequently used items to keep them at the top of your dashboard for quick access. You can pin up to 15 items.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              gap: 2,
              p: 2.5,
              bgcolor: (theme) => theme.palette.mode === 'light' ? '#F5F5F5' : 'grey.800',
              borderRadius: 2
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: '#1976d2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1rem'
              }}
            >
              1
            </Box>
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 'bold', mb: 0.5, color: theme.palette.text.primary }}
              >
                To Pin an Item
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                Click the pin icon in the top-right corner of any card. The pin will fill with blue and rotate, and the item will move to the top of your dashboard.
              </Typography>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              gap: 2,
              p: 2.5,
              bgcolor: (theme) => theme.palette.mode === 'light' ? '#F5F5F5' : 'grey.800',
              borderRadius: 2
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: '#1976d2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1rem'
              }}
            >
              2
            </Box>
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 'bold', mb: 0.5, color: theme.palette.text.primary }}
              >
                To Unpin an Item
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                Click the blue pin icon on any pinned item. It will return to its outline state and the item will move back to its normal position.
              </Typography>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              gap: 2,
              p: 2.5,
              bgcolor: (theme) => theme.palette.mode === 'light' ? '#F5F5F5' : 'grey.800',
              borderRadius: 2
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: '#1976d2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1rem'
              }}
            >
              3
            </Box>
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 'bold', mb: 0.5, color: theme.palette.text.primary }}
              >
                Organize Your Workspace
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                Pin multiple items to create your personalized quick-access dashboard. Pinned items stay at the top across all your sessions.
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

const FeatureLayout = ({ features, onFeatureClick, onFeatureHover, title }) => {
  const isHomeDashboard = title === "Home Dashboard";
  const [pinnedFeatures, setPinnedFeatures] = useState([]);
  const [showPersistenceWarning, setShowPersistenceWarning] = useState(false);
  const theme = useTheme();
  
  // Environment-specific localStorage key
  const environment = import.meta.env.VITE_ENVIRONMENT || 'Local';
  const storageKey = `pinnedFeatures_${environment}`;

  const refreshPinnedFeatures = useCallback(() => {
    const storedValue = persistentStorageService.getItem(storageKey);
    const stored = storedValue ? JSON.parse(storedValue) : [];
    
    console.log('[FeatureLayout] Refreshing pinned features:', {
      environment,
      storageKey,
      storedIds: stored,
      totalFeatures: features.length,
      availableFeatureIds: features.map(f => f.id)
    });
    
    const pinned = features.filter((f) => stored.includes(f.id)).sort((a, b) => stored.indexOf(a.id) - stored.indexOf(b.id));
    console.log('[FeatureLayout] Pinned features found:', pinned.length, pinned.map(p => p.title));

    setPinnedFeatures(pinned);
    
    // Check if we should show persistence warning
    if (persistentStorageService.shouldShowPersistenceWarning()) {
      setShowPersistenceWarning(true);
    }
  }, [features, storageKey, environment]);

  const handleReorder = useCallback((reorderedFeatures) => {
    if (!Array.isArray(reorderedFeatures)) {
      console.error('handleReorder: reorderedFeatures must be an array');
      return;
    }
    
    // Validate array length matches
    if (reorderedFeatures.length !== pinnedFeatures.length) {
      console.error('handleReorder: reorderedFeatures length mismatch');
      return;
    }
    
    // Validate all features have IDs
    if (reorderedFeatures.some(f => !f || !f.id)) {
      console.error('handleReorder: all features must have an id property');
      return;
    }
    
    // Validate it's the same set of features (just reordered)
    const originalIds = new Set(pinnedFeatures.map(f => f.id));
    const reorderedIds = new Set(reorderedFeatures.map(f => f.id));
    if (originalIds.size !== reorderedIds.size || 
        !Array.from(originalIds).every(id => reorderedIds.has(id))) {
      console.error('handleReorder: reorderedFeatures must contain the same features');
      return;
    }
    
    try {
      // Update the order in persistent storage
      const newOrder = reorderedFeatures.map(f => f.id);
      persistentStorageService.setItem(storageKey, JSON.stringify(newOrder));
      
      // Update local state
      setPinnedFeatures(reorderedFeatures);
    } catch (error) {
      console.error('Failed to save pinned features order:', error);
      setShowPersistenceWarning(true);
    }
  }, [pinnedFeatures, storageKey]);

  useEffect(() => {
    refreshPinnedFeatures();
  }, [refreshPinnedFeatures]);

  return (
    <Container maxWidth="xxl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
      {/* Storage Persistence Warning */}
      {isHomeDashboard && (
        <Collapse in={showPersistenceWarning}>
          <Alert
            severity="warning"
            icon={<WarningIcon />}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setShowPersistenceWarning(false)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            sx={{ mb: 2, mt: 2 }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              Pinned Items May Not Persist
            </Typography>
            <Typography variant="body2">
              Your browser settings may be clearing stored data when you close it. 
              Pinned items will work during your session but may not persist after closing the browser.
              We're using cookies as a backup, but check your browser's "Clear browsing data on close" settings.
            </Typography>
          </Alert>
        </Collapse>
      )}
      
      {isHomeDashboard ? (
        <Box sx={{ width: "100%", pt: 2, pb: 5 }}>
          {pinnedFeatures.length > 0 ? (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1,pb:2 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "bold",
                    color: theme.palette.text.primary,
                  }}
                >
                  Pinned
                </Typography>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    bgcolor: (theme) => theme.palette.mode === 'light' ? '#E3F2FD' : '#1e3a5f',
                    color: (theme) => theme.palette.mode === 'light' ? '#1976d2' : '#90caf9',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '12px',
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}
                >
                  <PushPinIcon sx={{ fontSize: 16 }} />
                  {pinnedFeatures.length} / 15
                </Box>
              </Box>
              <FeatureCardList
                features={pinnedFeatures}
                onFeatureClick={onFeatureClick}
                onFeatureHover={onFeatureHover}
                onPinChange={refreshPinnedFeatures}
                isDraggable={true}
                onReorder={handleReorder}
              />
              </Box>
          ) : (
            <PinnedItemsEmptyState />
          )}
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            pt: { xs: 2, sm: 3, md: 4 },
            pb: { xs: 4, sm: 5 },
          }}
        >
          <FeatureCardList
            features={features}
            onFeatureClick={onFeatureClick}
            onPinChange={refreshPinnedFeatures}
          />
        </Box>
      )
      }
    </Container >
  );
};

FeatureLayout.propTypes = {
  features: PropTypes.array.isRequired,
  onFeatureClick: PropTypes.func.isRequired,
  onFeatureHover: PropTypes.func,
  title: PropTypes.string.isRequired,
};

export default FeatureLayout;